/**
 * useCockpitContext — determines the current navigation scope
 * and builds a context string to send to the AI agent.
 *
 * On a machine view → includes machine number, type, line, factory
 * On a line view    → includes line name, factory
 * On home view      → general (no specific scope)
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSAPDataStore } from '../store/SAPDataStore';

export interface CockpitContext {
  /** 'machine' | 'line' | 'factory' | 'general' */
  scope: 'machine' | 'line' | 'general';
  /** Human-readable context string injected into chat messages */
  contextString: string;
  /** Raw identifiers for display */
  label: string;
}

export function useCockpitContext(): CockpitContext {
  const location = useLocation();
  const { machines, lines } = useSAPDataStore();

  return useMemo(() => {
    const path = location.pathname;

    // ── Machine view ───────────────────────────────────────────────────────
    if (path.startsWith('/machine/')) {
      const machineIdFromPath = path.split('/machine/')[1];
      const machine = machines.find(
        (m) =>
          m.maschinennummer === machineIdFromPath ||
          m.maschineBK === machineIdFromPath ||
          m.machineId === machineIdFromPath,
      );

      if (machine) {
        const line = lines.find(
          (l) => l.linieBK === machine.lineId || l.lineId === machine.lineId,
        );
        
        // Extract machine type - remove machine number prefix if present (e.g., "20056 - Modell 401" -> "Modell 401")
        let machineType = machine.bautyp || machine.machineType || '';
        const machineNum = machine.maschinennummer || machine.maschineBK || '';
        if (machineType && machineNum && machineType.startsWith(machineNum)) {
          // Remove the machine number prefix and separator (e.g., "20056 - " or "20056 ")
          machineType = machineType.replace(new RegExp(`^${machineNum}\\s*[-–]?\\s*`), '').trim();
        }
        
        const parts = [
          `Machine: ${machine.maschinennummer || machine.maschineBK}`,
          machineType ? `Type: ${machineType}` : '',
          line ? `Line: ${line.linieNummer && line.linieName ? `${line.linieNummer} - ${line.linieName}` : (line.linieName || line.lineId)}` : '',
        ].filter(Boolean);

        return {
          scope: 'machine' as const,
          contextString: `[Context: ${parts.join(', ')}]`,
          label: machine.maschinennummer || machine.maschineBK || machineIdFromPath,
        };
      }

      return {
        scope: 'machine' as const,
          contextString: `[Context: Machine: ${machineIdFromPath}]`,
        label: machineIdFromPath,
      };
    }

    // ── Line view ──────────────────────────────────────────────────────────
    if (path.startsWith('/line/')) {
      const lineIdFromPath = path.split('/line/')[1];
      const line = lines.find((l) => l.lineId === lineIdFromPath);

      if (line) {
        const lineParts = [
          `Line: ${line.linieNummer && line.linieName ? `${line.linieNummer} - ${line.linieName}` : (line.linieName || line.lineId)}`,
        ];

        return {
          scope: 'line' as const,
          contextString: `[Context: ${lineParts.join(', ')}]`,
          label: line.linieName || lineIdFromPath,
        };
      }

      return {
        scope: 'line' as const,
        contextString: `[Context: Line: ${lineIdFromPath}]`,
        label: lineIdFromPath,
      };
    }

    // ── Home / general ─────────────────────────────────────────────────────
    return {
      scope: 'general' as const,
      contextString: '[Context: Overview / Home page]',
      label: '',
    };
  }, [location.pathname, machines, lines]);
}
